import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import InputWithIcon from "../../../components/shared/fields/InputWithIcon";
import SelectBox from "../../../components/shared/fields/SelectBox ";
import Button from "../../../components/shared/buttons/button";
import useTranslator from "../../../hooks/useTranslator";
import { RefreshSvg } from "../../../svgFiles/RefreshSvg";
import { useCreateCartMaterialMutation, useUpdateCartMaterialMutation } from "./cartApi";
import { useGetMixingCartListQuery } from "../../settings/mixingCart/mixingCartApi";
import { convertToUserTimezone, getCurrentDateTime } from "../../../helpers/Utils";
import { EMPTY_MESH_WEIGHT_KG } from "../../../helpers/constants";
import { SETTINGS } from "../../../helpers/systemVariable";

const initialForm = {
  entered_date_time: getCurrentDateTime("YYYY-MM-DDTHH:mm"),
  mixing_cart_id:    "",
  no_of_treys:       "",
  weight_before:     "",
  weight_after:      "",
  actual_weight_before : 0,
  actual_weight_after : 0,
  moisture_percent:  "",
  water_activity:    "",
  evaporation_percentage : 0,
  status:            "at_rest",
};

const CartMaterialModal = ({ show, setShow, batchCode, record, mode = "add" }) => {
  // console.log(`Cart Material Modal opened`)
  const { _t } = useTranslator();
  const [formData, setFormData] = useState(initialForm);

  const STATUS_OPTIONS = [
    { value: "at_rest",       label: _t("status_at_rest",       "At Rest")       },
    { value: "cart_unloaded", label: _t("status_cart_unloaded", "Cart Unloaded") },
    // { value: "in_pasting",    label: _t("status_in_pasting",    "In Pasting")    },
    { value: "pasting_done",  label: _t("status_pasting_done",  "Pasting Done")  },
  ];

  const [createCartMaterial, { isLoading: isCreating }] = useCreateCartMaterialMutation();
  const [updateCartMaterial, { isLoading: isUpdating }] = useUpdateCartMaterialMutation();
  const { data: cartListData } = useGetMixingCartListQuery({}, { skip: !show });

  const isView = mode === "view" || record?.status === "pasting_done";

  // Populate form on edit/view
  useEffect(() => {
    if (show) {
      if ((mode === "edit" || mode === "view") && record) {
        setFormData({
          entered_date_time: record.entered_date_time
            ? convertToUserTimezone(record.entered_date_time, "YYYY-MM-DDTHH:mm")
            : getCurrentDateTime("YYYY-MM-DDTHH:mm"),
          mixing_cart_id:   record.mixing_cart_id   || "",
          no_of_treys:      record.no_of_treys      || "",
          weight_before:    record.weight_before     || record.total_actual_weight || "",
          weight_after:     record.weight_after      || "",
          moisture_percent: record.moisture_percent  || "",
          water_activity:   record.water_activity    || "",
          actual_weight_before : record.actual_weight_before || "",
          actual_weight_after : record.actual_weight_after || "",
          evaporation_percentage :   record.evaporation_percentage    || "",
          status:           record.status            || "at_rest",
        });
      } else {
        setFormData({ ...initialForm, entered_date_time: getCurrentDateTime("YYYY-MM-DDTHH:mm") });
      }
    }
  }, [show, record, mode]);


  // ################# Calculate Actual Weight Before/After + Evaporation ###################

  useEffect(() => {
    const weightBefore = parseFloat(formData.weight_before);
    const weightAfter  = parseFloat(formData.weight_after);
    const treys        = parseFloat(formData.no_of_treys) || 0;

    // Get selected cart's empty_weight from the list
    const selectedCart = cartListData?.data?.find((c) => c.id == formData.mixing_cart_id);
    const emptyCartWeight = parseFloat(selectedCart?.empty_weight) || 0;

    // actual = raw weight - empty cart - (treys × mesh weight)
    const trayDeduction = treys * Number(SETTINGS.EMPTY_MESH_WEIGHT_KG);

    const actualBefore = !isNaN(weightBefore)
      ? parseFloat((weightBefore - emptyCartWeight - trayDeduction).toFixed(2))
      : 0;

    const actualAfter = !isNaN(weightAfter)
      ? parseFloat((weightAfter - emptyCartWeight - trayDeduction).toFixed(2))
      : 0;

    let evaporation_percentage = 0;
    if (!isNaN(weightBefore) && !isNaN(weightAfter) && weightBefore > 0) {
      evaporation_percentage = Number(
        (((weightBefore - weightAfter) / weightBefore) * 100).toFixed(2)
      );
    }

    setFormData((prev) => ({
      ...prev,
      actual_weight_before:    actualBefore >= 0 ? actualBefore : 0,
      actual_weight_after:     actualAfter  >= 0 ? actualAfter  : 0,
      evaporation_percentage,
    }));
  }, [formData.weight_before, formData.weight_after, formData.no_of_treys, formData.mixing_cart_id, cartListData]);

  const handleClose = () => setShow(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Enforce max 2 decimal places for weight fields
    if ((name === "weight_before" || name === "weight_after") && value !== "") {
      const parts = value.split(".");
      if (parts[1] && parts[1].length > 2) return;
    }
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCreating || isUpdating) return;
    const payload = {
      ...formData,
      batch_code:       batchCode,
      no_of_treys:      parseFloat(formData.no_of_treys)      || 0,
      weight_before:    parseFloat(formData.weight_before)    || 0,
      weight_after:     parseFloat(formData.weight_after)     || 0,
      moisture_percent: parseFloat(formData.moisture_percent) || 0,
      water_activity:   parseFloat(formData.water_activity)   || 0,
      evaporation_percentage : parseFloat(formData.evaporation_percentage)   || 0
    };
    try {
      if (mode === "edit") {
        await updateCartMaterial({ id: record.id, requestBody: payload }).unwrap();
      } else {
        await createCartMaterial(payload).unwrap();
      }
      handleClose();
    } catch (err) {
      console.error("Error saving cart material:", err);
    }
  };

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={
        isView
          ? _t("view_material", "View Material")
          : mode === "edit"
          ? _t("edit_material", "Edit Material")
          : _t("add_material", "Add Material")
      }
    >
      <Row>
        <Col lg={6}>
          <InputWithLabel
            label={_t("entered_date_time", "Entered Date Time")}
            type="datetime-local"
            name="entered_date_time"
            value={formData.entered_date_time}
            onChange={handleChange}
            disabled={true}
          />
        </Col>
    </Row>
    <Row>
        <Col lg={6}>
          <SelectBox
            label={_t("select_cart", "Select Cart")}
            name="mixing_cart_id"
            value={formData.mixing_cart_id}
            options={(cartListData?.data || []).map((c) => ({
              value: c.id,
              label: c.trays_added
                ? `${c.name} (${c.trays_added} ${_t("trays", "Trays")} / ${c.cart_weight} ${_t("kg", "KG")})`
                : c.name,
              disabled: !c.is_available,
            }))}
            onChange={handleChange}
            disabled={isView}
          />
        </Col>

        <Col lg={6}>
          <InputWithLabel
            label={_t("no_of_treys", "No. of Treys")}
            type="number"
            name="no_of_treys"
            value={formData.no_of_treys}
            onChange={handleChange}
            placeholder="0"
            disabled={isView}
          />
        </Col>

        <Col lg={6}>
          <InputWithIcon
            label={_t("weight_before", "Weight Before")}
            type="number"
            name="weight_before"
            value={formData.weight_before}
            onChange={handleChange}
            placeholder="0"
            step="0.01"
            icon={RefreshSvg}
            disabled={isView}
          />
        </Col>

        <Col lg={6}>
          <InputWithIcon
            label={_t("actual_weight_before", "Actual Weight Before")}
            type="number"
            name="actual_weight_before"
            value={formData.actual_weight_before}
            onChange={handleChange}
            placeholder="0"
            // icon={RefreshSvg}
            disabled={true}
          />
        </Col>

        <Col lg={6}>
          <InputWithIcon
            label={_t("weight_after", "Weight After")}
            type="number"
            name="weight_after"
            value={formData.weight_after}
            onChange={handleChange}
            placeholder="0"
            step="0.01"
            icon={RefreshSvg}
            disabled={isView}
          />
        </Col>

        <Col lg={6}>
          <InputWithIcon
            label={_t("actual_weight_after", "Actual Weight After")}
            type="number"
            name="actual_weight_after"
            value={formData.actual_weight_after}
            onChange={handleChange}
            placeholder="0"
            // icon={RefreshSvg}
            disabled={true}
          />
        </Col>

        <Col lg={6}>
          <InputWithLabel
            label={_t("moisture_percent", "% Moisture")}
            type="number"
            name="moisture_percent"
            value={formData.moisture_percent}
            onChange={handleChange}
            placeholder="0"
            disabled={isView}
          />
        </Col>

        <Col lg={6}>
          <InputWithLabel
            label={_t("water_activity", "Water Activity")}
            type="number"
            name="water_activity"
            value={formData.water_activity}
            onChange={handleChange}
            placeholder="0"
            disabled={isView}
          />
        </Col>

        <Col lg={6}>
          <InputWithLabel
            label={_t("evaporation_prcnt", "Evaportation %")}
            type="number"
            name="evaporation_percentage"
            value={formData.evaporation_percentage}
            placeholder="0"
            disabled={true}
          />
        </Col>

        <Col lg={6}>
          <SelectBox
            label={_t("status", "Status")}
            name="status"
            value={formData.status}
            onChange={handleChange}
            options={STATUS_OPTIONS}
            disabled={isView}
          />
        </Col>
      </Row>

      {!isView && (
        <Row>
          <Col className="text-end">
            <Button
              label={_t("save_changes", "Save Changes")}
              size="small"
              onClick={handleSubmit}
              disabled={isCreating || isUpdating}
            />
          </Col>
        </Row>
      )}
    </CommonModal>
  );
};

export default CartMaterialModal;
